import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  DistributionContent,
  ExperienceExternalCatalog,
  ExperienceOverview,
  ExperienceSearchFilter,
  ExternalCatalogLinkRequestDto,
  MediaSnapshot,
  OperationalExperienceOption,
  OperationalExperienceSummary,
  RawElement,
  RawSnapshot,
  TranslationSnapshot,
} from "@/types/generated/ExperienceRawServiceApi";
import { AsxExperience, ModalityCodes } from "@/features/experience-raw/legacy-asx-types";
import { Snapshot } from "@/types/Snapshots";
import { useAuthStore } from "@/features/auth/useAuthStore";
import { createHttpInstance } from "@/features/core-shared/utils/http/http";
import { Options } from "@/types/Http";
import { getIdFromLocation } from "@/features/core-shared/utils/http/http-utils";
import { emit } from "@rsql/emitter";
import builder from "@rsql/builder";
import { AvailableLanguage } from "@/types/Language";

export type UseExperienceRawApi = {
  _axiosInstance: AxiosInstance;
  getRawExperience: <T extends RawElement>(
    url: `experience-raw/${string}`,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  getRawExperiences: <T extends RawElement[]>(
    url: `experience-raw`,
    config?: AxiosRequestConfig
  ) => Promise<AxiosResponse<T>>;

  putRawExperience: <T extends RawElement>(url: `experience-raw/${string}`, data: T) => Promise<AxiosResponse>;

  postRawExperience: <T extends RawElement>(url: `experience-raw`, data: T) => Promise<AxiosResponse>;

  deleteRawExperience: (url: `experience-raw/${string}`) => Promise<AxiosResponse>;
  createDistributionContent: ReturnType<typeof createDistributionContent>;
  getExperienceRawV2: ReturnType<typeof getExperienceRawV2>;
  getExperienceRawV2ByExperienceId: ReturnType<typeof getExperienceRawV2>;
  createExperienceRaw: ReturnType<typeof createExperienceRaw>;
  patchExperienceRaw: ReturnType<typeof patchExperienceRaw>;
  deleteDistributionContent: ReturnType<typeof deleteDistributionContent>;
  getDistributionContent: ReturnType<typeof getDistributionContent>;
  updateDistributionContent: ReturnType<typeof updateDistributionContent>;
  patchDistributionContent: ReturnType<typeof patchDistributionContent>;
  getAllRawSnapshots: ReturnType<typeof getAllRawSnapshots>;
  getMostRecentRawSnapshot: ReturnType<typeof getMostRecentRawSnapshot>;
  getMostRecentPublishedRawSnapshot: ReturnType<typeof getMostRecentPublishedRawSnapshot>;
  getAllSnapshots: ReturnType<typeof getAllSnapshots>;
  getAllTranslationSnapshots: ReturnType<typeof getAllTranslationSnapshots>;
  getMostRecentTranslationSnapshot: ReturnType<typeof getMostRecentTranslationSnapshot>;
  getAllMediaSnapshots: ReturnType<typeof getAllMediaSnapshots>;
  getSingleSnapshot: ReturnType<typeof getSingleSnapshot>;
  getLastTwoSnapshots: ReturnType<typeof getLastTwoSnapshots>;
  getLastSnapshotBeforeDate: ReturnType<typeof getLastSnapshotBeforeDate>;
  getSingleRawSnapshot: ReturnType<typeof getSingleRawSnapshot>;
  getAsterixServices: ReturnType<typeof getAsterixServices>;
  getAsterixModalities: ReturnType<typeof getAsterixModalities>;
  getExternalExperiences: ReturnType<typeof getExternalExperiences>;
  getAllExternalExperiences: ReturnType<typeof getAllExternalExperiences>;
  getLinkedExternalExperience: ReturnType<typeof getLinkedExternalExperience>;
  linkExternalExperience: ReturnType<typeof linkExternalExperience>;
  unlinkExternalExperience: ReturnType<typeof unlinkExternalExperience>;
  sendToReview: ReturnType<typeof sendToReview>;
  sendToReviewV3: ReturnType<typeof sendToReviewV3>;
  completeRawExperience: ReturnType<typeof completeRawExperience>;
  searchOperationalExperiences: ReturnType<typeof searchOperationalExperiences>;
  getOperationalExperienceOptions: ReturnType<typeof getOperationalExperienceOptions>;
  unlinkOperational: ReturnType<typeof unlinkOperational>;
  searchExperiencesSummary: ReturnType<typeof searchExperiencesSummary>;
};

export class SupplierWithoutEventsError extends Error {}

function createDistributionContent(http: AxiosInstance) {
  const SUPPLIER_NOT_FOUND_ERROR_CODE: string = "SUPPLIER_NOT_FOUND";

  return async (payload: Partial<DistributionContent>, options?: Options) => {
    const { config } = options || {};

    try {
      const { headers } = await http.post<DistributionContent[]>(`/distribution-content`, payload, config);

      const id = getIdFromLocation(headers?.location);

      return { data: id };
    } catch (err: any) {
      const errorCode = err?.response?.data?.code;
      if (errorCode === SUPPLIER_NOT_FOUND_ERROR_CODE) {
        throw new SupplierWithoutEventsError();
      }

      throw err;
    }
  };
}

function getExperienceRawV2ByExperienceId(http: AxiosInstance) {
  return async (experienceId: string, config?: AxiosRequestConfig) => {
    const { data } = await http.get<RawElement[]>(`/v2/experience-raw`, {
      params: {
        filters: emit(builder.eq("experience_id", experienceId)),
      },
      ...config,
    });
    return { data: data?.[0] };
  };
}

function getExperienceRawV2(http: AxiosInstance) {
  return async (rawId: string, config?: AxiosRequestConfig) => {
    const { data } = await http.get<RawElement>(`/v2/experience-raw/${rawId}`, config);
    return { data };
  };
}

function createExperienceRaw(http: AxiosInstance) {
  return async (experienceId: string, title: string, options?: Options) => {
    const { config } = options || {};

    const body: Omit<RawElement, "status_code" | "flow_code"> = {
      experience_id: experienceId,
      commercial: {
        title,
      },
    };

    const { headers } = await http.post(`/v2/experience-raw`, body, config);

    const id = getIdFromLocation(headers?.location);

    return { data: id };
  };
}

export type PatchExperienceRawPayload = Partial<RawElement>;

function patchExperienceRaw(http: AxiosInstance) {
  return async (rawId: string, payload: PatchExperienceRawPayload) => {
    // Deduplicate additional_services before sending to API
    if (payload.functional?.additional_services) {
      payload.functional.additional_services = [...new Set(payload.functional.additional_services)];
    }

    const { data } = await http.patch(`/experience-raw/${rawId}`, payload);
    return { data };
  };
}

function deleteDistributionContent(http: AxiosInstance) {
  return async (rawId: string) => {
    const { data } = await http.delete(`/distribution-content/${rawId}`);
    return { data };
  };
}

function getDistributionContent(http: AxiosInstance) {
  return async (experienceId: string, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.get<DistributionContent>(`/distribution-content/${experienceId}`, config);

    return { data };
  };
}

function updateDistributionContent(http: AxiosInstance) {
  return async (experienceId: string, payload: DistributionContent, options?: Options) => {
    const config = options || {};
    const { data } = await http.put<boolean>(`/distribution-content/${experienceId}`, payload, config);
    return { data };
  };
}

function patchDistributionContent(http: AxiosInstance) {
  return async (experienceId: string, payload: Partial<DistributionContent>, options?: Options) => {
    const { config } = options || {};

    const { data } = await http.patch<boolean>(`/distribution-content/${experienceId}`, payload, config);

    return { data };
  };
}

function getAllRawSnapshots(http: AxiosInstance) {
  return async (experienceId: string) => {
    const { data } = await http.get<RawSnapshot[]>(`/raw-snapshot`, {
      params: {
        filters: emit(builder.eq("raw.experience_id", experienceId)),
        sort: "-snapshotDate",
      },
    });

    return { data };
  };
}

function getSingleRawSnapshot(http: AxiosInstance) {
  return async (snapshotId: string) => {
    const { data } = await http.get<RawSnapshot>(`/raw-snapshot/${snapshotId}`);
    return { data };
  };
}

function getMostRecentPublishedRawSnapshot(http: AxiosInstance) {
  return async (experienceId: string): Promise<{ data: RawSnapshot | undefined }> => {
    const { data } = await http.get<RawSnapshot[]>(`/raw-snapshot`, {
      params: {
        filters: emit(
          // The filter searches for the latest published version of the raw data associated with the provided experience ID.
          // A raw snapshot with status "UP_TO_DATE" is created when the experience is published.
          builder.and(builder.eq("raw.experience_id", experienceId), builder.eq("raw.status_code", "UP_TO_DATE"))
        ),
        sort: "-snapshotDate",
        limit: 1,
      },
    });

    return { data: data[0] };
  };
}

function getMostRecentRawSnapshot(http: AxiosInstance) {
  return async (experienceId: string): Promise<{ data: RawSnapshot }> => {
    const { data } = await http.get<RawSnapshot[]>(`/raw-snapshot`, {
      params: {
        filters: emit(
          // The filter searches for the latest published or sent-to-review version of the raw data associated with the provided experience ID.
          // A raw snapshot is created when raw is sent to review (the status is "SENT_TO_REVIEW") or when the experience is
          // published (status "UP_TO_DATE")
          builder.and(
            builder.eq("raw.experience_id", experienceId),
            builder.or(builder.eq("raw.status_code", "UP_TO_DATE"), builder.eq("raw.status_code", "SENT_TO_REVIEW"))
          )
        ),
        sort: "-snapshotDate",
        limit: 1,
      },
    });

    return { data: data[0] };
  };
}

function getAllSnapshots(http: AxiosInstance) {
  return async (experienceId: string) => {
    const { data } = await http.get<Snapshot[]>(`/snapshot`, {
      params: {
        filters: emit(builder.eq("experience_id", experienceId)),
        sort: "-creationDate",
      },
    });

    return { data };
  };
}

function getAllTranslationSnapshots(http: AxiosInstance) {
  return async (experienceId: string, language_code: AvailableLanguage) => {
    const { data } = await http.get<Snapshot[]>(`/translation-snapshot`, {
      params: {
        experience_id: experienceId,
        language_code,
        sort: "-creationDate",
      },
    });

    return { data };
  };
}

function getMostRecentTranslationSnapshot(http: AxiosInstance) {
  return async (
    experienceId: string,
    language_code: AvailableLanguage
  ): Promise<{ data: TranslationSnapshot | undefined }> => {
    const { data } = await http.get<Snapshot[]>(`/translation-snapshot`, {
      params: {
        experience_id: experienceId,
        language_code,
        sort: "-creationDate",
      },
    });

    return { data: data[0] };
  };
}

function getAllMediaSnapshots(http: AxiosInstance) {
  return async (experienceId: string) => {
    const { data } = await http.get<MediaSnapshot[]>(`/media-snapshot`, {
      params: {
        experience_id: experienceId,
        sort: "-creationDate",
      },
    });

    return { data };
  };
}

function getSingleSnapshot(http: AxiosInstance) {
  return async (snapshotId: string) => {
    const { data } = await http.get<Snapshot>(`/snapshot/${snapshotId}`);
    return { data };
  };
}

function getLastTwoSnapshots(http: AxiosInstance) {
  return async (experienceId: string) => {
    const { data } = await http.get<Snapshot[]>(`/snapshot`, {
      params: {
        filters: emit(
          builder.and(
            builder.eq("experience_id", experienceId),
            builder.and(
              builder.eq("experienceCommercialInformation.translations.languageCode", "en"),
              builder.eq("experienceCommercialInformation.translations.experienceTranslation.statusCode", "READY")
            )
          )
        ),
        sort: "-creationDate",
        limit: 2,
      },
    });

    return { data };
  };
}

function getLastSnapshotBeforeDate(http: AxiosInstance) {
  return async (experienceId: string, date: Date): Promise<{ data: Snapshot | undefined }> => {
    const { data } = await http.get<Snapshot[]>(`/snapshot`, {
      params: {
        filters: emit(
          builder.and(
            builder.eq("experience_id", experienceId),
            builder.and(
              builder.eq("experienceCommercialInformation.translations.languageCode", "en"),
              builder.eq("experienceCommercialInformation.translations.experienceTranslation.statusCode", "READY")
            ),
            builder.le("creationDate", date.toISOString())
          )
        ),
        sort: "-creationDate",
        limit: 1,
      },
    });

    return { data: data[0] };
  };
}

function getAsterixServices(http: AxiosInstance) {
  return async (query: string, offset: number, limit: number) => {
    const { data } = await http.get<AsxExperience[]>(`/asx/experiences`, {
      params: { contain: query, offset, limit },
    });

    return { data };
  };
}

function getAsterixModalities(http: AxiosInstance) {
  return async (asterixServiceCode: string, offset: number, limit: number) => {
    const { data } = await http.get<ModalityCodes[]>(`/asx/experiences/${asterixServiceCode}/modalities`, {
      params: { offset, limit },
    });

    return { data };
  };
}

function getExternalExperiences(http: AxiosInstance) {
  return async (supplier_id: string) => {
    const { data } = await http.get<ExperienceExternalCatalog[]>(`/external-experiences`, {
      params: { filters: emit(builder.eq("supplier_id", supplier_id)) },
    });

    return { data };
  };
}

function getAllExternalExperiences(http: AxiosInstance) {
  return async () => {
    const { data } = await http.get<ExperienceExternalCatalog[]>(`/external-experiences`);

    return { data };
  };
}

function getLinkedExternalExperience(http: AxiosInstance) {
  return async (experience_id: string) => {
    const { data } = await http.get<ExperienceExternalCatalog[]>(`/external-experiences`, {
      params: {
        filters: emit(builder.and(builder.eq("linked", "true"), builder.eq("experience_id", experience_id))),
      },
    });

    return { data };
  };
}

function linkExternalExperience(http: AxiosInstance) {
  return async (id: string, payload: ExternalCatalogLinkRequestDto) => {
    const { data } = await http.post(`/external-experiences/${id}/link`, payload);
    return { data };
  };
}

function unlinkExternalExperience(http: AxiosInstance) {
  return async (id: string) => {
    const { data } = await http.post(`/external-experiences/${id}/unlink`);
    return { data };
  };
}

function sendToReview(http: AxiosInstance) {
  return async (rawId: string) => {
    const { data } = await http.post(`/experience-raw/${rawId}/send-to-review`, null, {
      headers: { "accept-version": "vnd.experience-raw-offer-service.v2" },
    });
    return { data };
  };
}

function sendToReviewV3(http: AxiosInstance) {
  return async (rawId: string) => {
    const { data } = await http.post(`/experience-raw/${rawId}/send-to-review`, null, {
      headers: { "accept-version": "vnd.experience-raw-offer-service.v3" },
    });
    return { data };
  };
}

function completeRawExperience(http: AxiosInstance) {
  return async (rawId: string) => {
    const { data } = await http.post(`/experience-raw/${rawId}/complete`, null, {
      headers: { "accept-version": "vnd.experience-raw-offer-service.v2" },
    });
    return { data };
  };
}

function searchOperationalExperiences(http: AxiosInstance) {
  return async (code: string, options?: { offset?: number; limit?: number }) => {
    const { data } = await http.get<OperationalExperienceSummary[]>(`/operational-experiences`, {
      params: {
        code,
        offset: options?.offset ?? 0,
        limit: options?.limit ?? 50,
      },
    });
    return { data };
  };
}

function getOperationalExperienceOptions(http: AxiosInstance) {
  return async (operationalId: string) => {
    const { data } = await http.get<OperationalExperienceOption[]>(`/operational-experiences/${operationalId}/options`);
    return { data };
  };
}

function unlinkOperational(http: AxiosInstance) {
  return async (rawId: string, operationalId: string) => {
    const { data } = await http.post<RawElement>(`/experience-raw/${rawId}/operational/${operationalId}/unlink`, null, {
      headers: { "accept-version": "vnd.experience-raw-offer-service.v2" },
    });
    return { data };
  };
}

/**
 * Filters accepted by `POST /experiences-summary/search`.
 *
 * Every field is single-value and the endpoint ANDs them together, matching the generated
 * {@link ExperienceSearchFilter}. The deviations are `publication_status`, which replaced
 * `global_status` in the service, and `supplier_id`; both are supported by the service but not in
 * the OpenAPI spec on master yet - drop these once `npm run generate:experience-raw` produces them.
 *
 * `experience_source` is widened from its enum to `string` because the value travels through the
 * generic filter panel, which types every field value as a string; the option list is what
 * guarantees it is one of the four valid sources.
 */
export type ExperienceSummarySearchFilter = Omit<ExperienceSearchFilter, "global_status" | "experience_source"> & {
  publication_status?: string;
  experience_source?: string;
  /** Filter by supplier UUID (matches the advanced-search `supplier_id` attribute). */
  supplier_id?: string;
};

function searchExperiencesSummary(http: AxiosInstance) {
  return async (filters: ExperienceSummarySearchFilter) => {
    const { data, headers } = await http.post<ExperienceOverview[]>(`/experiences-summary/search`, filters);
    const totalCount = Number(headers["x-total-count"] || headers["total-count"] || data.length);
    return { data, totalCount };
  };
}

/**
 * Experience Raw Service interface
 * @returns _axiosInstance - Original axios instance, to be used only for internal purpose
 * @returns getRawExperience - Public method for get requests, TS already configured
 * @returns getRawExperiences - Public method for get requests, TS already configured (return an array)
 * @returns putRawExperience - Public method for put requests, TS already configured
 * @returns postRawExperience - Public method for post requests, TS already configured
 */
export function useExperienceRawApi(): UseExperienceRawApi {
  const config = useRuntimeConfig();
  const auth = useAuthStore();

  const axiosInstance = createHttpInstance({
    config: {
      baseURL: config.public.EXPERIENCE_RAW_SERVICE_BASE_URL,
      headers: { "accept-version": "vnd.experience-raw-offer-service.v1" },
    },
    auth,
  });

  const axiosInstanceV2 = createHttpInstance({
    config: {
      baseURL: config.public.EXPERIENCE_RAW_SERVICE_BASE_URL,
      headers: { "accept-version": "vnd.experience-raw-offer-service.v2" },
    },
    auth,
  });

  const putRawExperience = <T extends RawElement>(url: `experience-raw/${string}`, data: T) => {
    if (data.functional?.additional_services) {
      data.functional.additional_services = [...new Set(data.functional.additional_services)];
    }
    return axiosInstance.put(url, data);
  };

  const postRawExperience = <T extends RawElement>(url: `experience-raw`, data: T) => {
    if (data.functional?.additional_services) {
      data.functional.additional_services = [...new Set(data.functional.additional_services)];
    }
    return axiosInstance.post(url, data);
  };

  return {
    _axiosInstance: axiosInstance,
    getRawExperience: axiosInstance.get,
    getRawExperiences: axiosInstance.get,
    putRawExperience,
    postRawExperience,
    deleteRawExperience: axiosInstance.delete,
    createDistributionContent: createDistributionContent(axiosInstance),
    getExperienceRawV2: getExperienceRawV2(axiosInstance),
    getExperienceRawV2ByExperienceId: getExperienceRawV2ByExperienceId(axiosInstance),
    createExperienceRaw: createExperienceRaw(axiosInstance),
    patchExperienceRaw: patchExperienceRaw(axiosInstanceV2),
    deleteDistributionContent: deleteDistributionContent(axiosInstance),
    getDistributionContent: getDistributionContent(axiosInstance),
    updateDistributionContent: updateDistributionContent(axiosInstance),
    patchDistributionContent: patchDistributionContent(axiosInstance),
    getMostRecentRawSnapshot: getMostRecentRawSnapshot(axiosInstance),
    getMostRecentPublishedRawSnapshot: getMostRecentPublishedRawSnapshot(axiosInstance),
    getAllRawSnapshots: getAllRawSnapshots(axiosInstance),
    getLastTwoSnapshots: getLastTwoSnapshots(axiosInstance),
    getLastSnapshotBeforeDate: getLastSnapshotBeforeDate(axiosInstance),
    getAllSnapshots: getAllSnapshots(axiosInstance),
    getAllTranslationSnapshots: getAllTranslationSnapshots(axiosInstance),
    getMostRecentTranslationSnapshot: getMostRecentTranslationSnapshot(axiosInstance),
    getAllMediaSnapshots: getAllMediaSnapshots(axiosInstance),
    getSingleSnapshot: getSingleSnapshot(axiosInstance),
    getSingleRawSnapshot: getSingleRawSnapshot(axiosInstance),
    getAsterixServices: getAsterixServices(axiosInstance),
    getAsterixModalities: getAsterixModalities(axiosInstance),
    getExternalExperiences: getExternalExperiences(axiosInstance),
    getAllExternalExperiences: getAllExternalExperiences(axiosInstance),
    getLinkedExternalExperience: getLinkedExternalExperience(axiosInstance),
    linkExternalExperience: linkExternalExperience(axiosInstance),
    unlinkExternalExperience: unlinkExternalExperience(axiosInstance),
    sendToReview: sendToReview(axiosInstance),
    sendToReviewV3: sendToReviewV3(axiosInstance),
    completeRawExperience: completeRawExperience(axiosInstance),
    searchOperationalExperiences: searchOperationalExperiences(axiosInstance),
    getOperationalExperienceOptions: getOperationalExperienceOptions(axiosInstance),
    unlinkOperational: unlinkOperational(axiosInstanceV2),
    searchExperiencesSummary: searchExperiencesSummary(axiosInstance),
  };
}
